import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Divider,
  Paper,
  Stack,
  Chip,
  Button,
  useMediaQuery,
} from "@mui/material";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import TipsAndUpdatesRoundedIcon from "@mui/icons-material/TipsAndUpdatesRounded";
import HeadphonesRoundedIcon from "@mui/icons-material/HeadphonesRounded";
import MenuBookRoundedIcon from "@mui/icons-material/MenuBookRounded";
import GraphicEqRoundedIcon from "@mui/icons-material/GraphicEqRounded";
import CompressRoundedIcon from "@mui/icons-material/CompressRounded";
import LibraryMusicRoundedIcon from "@mui/icons-material/LibraryMusicRounded";
import SpeedRoundedIcon from "@mui/icons-material/SpeedRounded";
import BoltRoundedIcon from "@mui/icons-material/BoltRounded";
import { Link as RouterLink } from "react-router-dom";
import { useStyle } from "../../context/styleContext/styleContext";
import Seo from "../../components/Seo";
import ArticleSchemaMarkup from "./ArticleSchemaMarkup.jsx";

export default function TightenLowEndLearning() {
  const { style } = useStyle();
  const isMobile = useMediaQuery("(max-width:768px)");
  const [height, setHeight] = useState(window.innerHeight);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, []);

  useEffect(() => {
    function handleResize() {
      setHeight(window.innerHeight);
    }

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const accent = "rgb(88, 166, 124)";

  const quickFacts = [
    {
      title: "What tight low end means",
      text: "Low frequencies feel controlled, focused, and connected to the groove instead of loose, cloudy, or slow.",
      icon: <BoltRoundedIcon sx={{ color: accent }} />,
    },
    {
      title: "Where problems start",
      text: "Guitars, bass, kick, room tone, palm mutes, cab resonance, and too much low-mid buildup can all blur the low end.",
      icon: <HeadphonesRoundedIcon sx={{ color: accent }} />,
    },
    {
      title: "Best first move",
      text: "Decide which instrument owns the deepest range, then clear space around it instead of making everything bigger.",
      icon: <TuneRoundedIcon sx={{ color: accent }} />,
    },
  ];

  const commonProblems = [
    {
      title: "Palm mutes sound huge but messy",
      description:
        "Heavy guitars can feel exciting in solo but turn loose in a full mix when low-end resonance piles up around the kick and bass.",
    },
    {
      title: "Bass has weight but no definition",
      description:
        "A bass part can have plenty of low end while still feeling blurry if the note definition, midrange, or dynamics are not controlled.",
    },
    {
      title: "Kick and bass fight each other",
      description:
        "When both instruments are trying to own the same low-frequency space, the groove can feel smaller even though the low end is loud.",
    },
    {
      title: "The whole mix feels cloudy",
      description:
        "Low-end mud often comes from many small sources adding up: guitars, keys, reverbs, rooms, and effects returns all carrying unnecessary lows.",
    },
  ];

  const steps = [
    {
      title: "Start with the arrangement",
      description:
        "Tight low end starts before processing. If the bass, kick, low guitars, synths, and effects all fill the same space, no plugin can fully clean that up. Decide what needs to be big, what needs to be supportive, and what can be trimmed.",
    },
    {
      title: "Choose who owns the sub range",
      description:
        "In most mixes, the deepest low end should be intentional. Sometimes the kick owns the deepest hit and the bass sits slightly above it. Other times the bass carries the bottom and the kick provides attack. Pick a role instead of letting both fight.",
    },
    {
      title: "High-pass what does not need true low end",
      description:
        "Guitars, vocals, keys, effects returns, and room mics often carry low-frequency energy that does not help the mix. A careful high-pass filter can make the low end tighter without making the track thin.",
    },
    {
      title: "Control the low mids",
      description:
        "The 150–400 Hz range can add body, but it is also where mud builds quickly. If guitars or bass feel thick but unclear, small cuts in the low mids can create focus while keeping the weight intact.",
    },
    {
      title: "Separate bass weight from bass definition",
      description:
        "The listener needs to feel the bass and understand the notes. Low frequencies provide weight, but the midrange often provides the part’s identity. If the bass disappears on smaller speakers, it may need more definition, not more sub.",
    },
    {
      title: "Tighten guitar lows before the amp or cab stage",
      description:
        "For distorted guitars, too much low end before distortion can create fizz, flub, and smeared palm mutes. Filtering or tightening before heavy gain can make the distortion react in a more controlled way.",
    },
    {
      title: "Use compression carefully",
      description:
        "Compression can stabilize bass and low instruments, but too much can make the low end feel flat or slow. Watch the attack and release so the groove still breathes.",
    },
    {
      title: "Clean up reverbs and effects",
      description:
        "Reverb, delay, and modulation returns can carry hidden low end. High-pass effects returns so they add space and movement without clouding the foundation of the mix.",
    },
    {
      title: "Check the low end quietly",
      description:
        "A loose low end often becomes obvious at low volume. If the groove disappears or the guitars feel cloudy when the mix is quiet, the low end probably needs more focus.",
    },
  ];

  const frequencyAreas = [
    {
      range: "30–60 Hz",
      title: "Sub weight",
      description:
        "Adds depth and physical size. Too much can eat headroom and make the mix feel unstable.",
    },
    {
      range: "60–120 Hz",
      title: "Kick and bass foundation",
      description:
        "This is where much of the weight lives. The kick and bass need clear roles here.",
    },
    {
      range: "120–250 Hz",
      title: "Warmth and thickness",
      description:
        "Important for body, but too much can make guitars, bass, and drums feel slow or congested.",
    },
    {
      range: "250–500 Hz",
      title: "Mud and boxiness",
      description:
        "A common buildup area where low instruments and dense arrangements start to blur together.",
    },
    {
      range: "700 Hz–2 kHz",
      title: "Bass and guitar definition",
      description:
        "Can help notes read more clearly, especially on smaller speakers and dense mixes.",
    },
  ];

  const tools = [
    {
      title: "High-pass filtering",
      description:
        "Removes unnecessary low-frequency energy from tracks that do not need to carry the foundation.",
    },
    {
      title: "Subtractive EQ",
      description:
        "Clears low-mid buildup and creates room for the instruments that actually need weight.",
    },
    {
      title: "Dynamic EQ",
      description:
        "Controls low-end buildup only when it gets excessive, which can be useful for palm mutes, bass notes, and resonant sections.",
    },
    {
      title: "Compression",
      description:
        "Helps stabilize bass and low-end movement, but needs the right timing to avoid flattening the groove.",
    },
    {
      title: "Saturation",
      description:
        "Adds harmonic detail so bass and guitars stay audible without only relying on deep lows.",
    },
  ];

  const bestPractices = [
    "Do not judge low end from solo tracks. Always check it against the full mix.",
    "High-pass carefully. Removing too much low end can make the mix small instead of tight.",
    "Let one element own the deepest range instead of stacking several instruments there.",
    "Use midrange definition to help bass and guitar translate on smaller speakers.",
    "Clean up reverb and delay returns so effects do not cloud the low end.",
    "Use dynamic control when low-end problems only happen on certain notes or sections.",
    "Check the mix at low volume and on small speakers to hear whether the low end is focused.",
  ];

  const relatedLinks = [
    { label: "Equalization", to: "/learning/equalization" },
    { label: "Dynamic EQ", to: "/learning/dynamic-eq" },
    { label: "Gain Staging", to: "/learning/gain-staging" },
    { label: "Saturation", to: "/learning/saturation" },
    { label: "How to Fix a Muddy Mix", to: "/learning/fix-muddy-mix" },
  ];

  const outerPanelSx = {
    width: "100%",
    minWidth: 0,
    borderRadius: "20px",
    overflow: "hidden",
    backgroundColor: style.color1,
    border: `1px solid ${style.color2}`,
    boxSizing: "border-box",
  };

  const subtlePanelSx = {
    borderRadius: "14px",
    backgroundColor: style.darkerPanel,
    border: "1px solid rgba(255,255,255,0.06)",
    boxShadow: "none",
  };

  return (
    <>
      <Seo
        title="How to Tighten Low End for Guitar and Bass | Parish Audio Learning"
        description="Learn how to tighten low end in guitar, bass, and full mixes with better arrangement, EQ, compression, saturation, and low-mid control."
        canonical="https://parishaudio.com/learning/how-to-tighten-low-end"
        image="https://parishaudio.com/RecordingStudioStockImage1.jpg"
        imageAlt="Recording studio background for a Parish Audio article about tightening low end"
      />

      <ArticleSchemaMarkup
        canonical="https://parishaudio.com/learning/how-to-tighten-low-end"
        headline="How to Tighten Low End for Guitar and Bass"
        description="Learn how to tighten low end in guitar, bass, and full mixes with better arrangement, EQ, compression, saturation, and low-mid control."
        image="https://parishaudio.com/RecordingStudioStockImage1.jpg"
        articleSection="Audio Education"
        keywords={[
          "tighten low end",
          "tight guitar low end",
          "tight bass mix",
          "muddy guitar tone",
          "low end mixing",
          "guitar mixing",
          "bass mixing",
          "EQ low end",
          "music production",
        ]}
        breadcrumbs={[
          { name: "Home", item: "https://parishaudio.com/" },
          { name: "Learning", item: "https://parishaudio.com/learning" },
          {
            name: "How to Tighten Low End",
            item: "https://parishaudio.com/learning/how-to-tighten-low-end",
          },
        ]}
        datePublished="2026-05-02T00:00:00-06:00"
        dateModified="2026-05-02T00:00:00-06:00"
      />

      <Box
        sx={{
          width: "100%",
          maxWidth: "100vw",
          overflowX: "hidden",
          backgroundColor: "black",
          px: { xs: 1.25, sm: 1.5, md: 2.5 },
          py: { xs: 1.5, md: 2.5 },
          boxSizing: "border-box",
          minHeight: height - 130,
        }}
      >
        <Box sx={{ width: "100%", maxWidth: 1120, mx: "auto", minWidth: 0 }}>
          <Stack spacing={{ xs: 2, md: 2.5 }}>
            <Paper
              elevation={0}
              sx={{
                ...outerPanelSx,
                minHeight: { xs: 280, md: 340 },
                display: "flex",
                alignItems: "center",
                backgroundImage:
                  'linear-gradient(90deg, rgba(8,12,18,0.86) 0%, rgba(8,12,18,0.75) 38%, rgba(8,12,18,0.46) 68%, rgba(8,12,18,0.22) 100%), url("/RecordingStudioStockImage1.jpg")',
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            >
              <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, width: "100%" }}>
                <Box sx={{ maxWidth: 820 }}>
                  <Stack spacing={1.25} alignItems="flex-start">
                    <Chip
                      label="Low-End Mixing Guide"
                      sx={{
                        borderRadius: "5px",
                        fontWeight: 700,
                        color: style.color4,
                        backgroundColor: "rgba(255,255,255,0.08)",
                        border: "1px solid rgba(255,255,255,0.10)",
                      }}
                    />

                    <Typography
                      variant="h1"
                      sx={{
                        fontSize: { xs: "2.15rem", md: "3.4rem" },
                        fontWeight: 800,
                        lineHeight: 1.02,
                        color: style.color4,
                        letterSpacing: "-0.04em",
                      }}
                    >
                      How to Tighten Low End for Guitar and Bass
                    </Typography>

                    <Typography
                      sx={{
                        color: "rgba(255,255,255,0.86)",
                        fontSize: { xs: "1rem", md: "1.1rem" },
                        lineHeight: 1.75,
                        maxWidth: 740,
                      }}
                    >
                      Tight low end is not about removing all the bass. It is about keeping
                      the low frequencies controlled, focused, and connected to the groove.
                      When guitars, bass, kick, and low mids work together, the mix feels
                      bigger without getting loose or cloudy.
                    </Typography>

                    <Typography sx={{ color: style.color3, fontSize: "0.9rem" }}>
                      Published May 2, 2026
                    </Typography>
                  </Stack>
                </Box>
              </Box>
            </Paper>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
                gap: 2,
                alignItems: "stretch",
              }}
            >
              {quickFacts.map((item) => (
                <Paper key={item.title} elevation={0} sx={{ ...outerPanelSx }}>
                  <Box sx={{ p: 2 }}>
                    <Stack direction="row" spacing={1.25} alignItems="flex-start">
                      {item.icon}
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          sx={{
                            fontWeight: 700,
                            color: style.color4,
                            mb: 0.5,
                            lineHeight: 1.2,
                          }}
                        >
                          {item.title}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: style.color3, lineHeight: 1.55 }}
                        >
                          {item.text}
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>
                </Paper>
              ))}
            </Box>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <MenuBookRoundedIcon sx={{ color: accent }} />
                    <Typography
                      sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                    >
                      What Makes Low End Feel Loose?
                    </Typography>
                  </Stack>

                  <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                    Loose low end usually happens when the bottom of the mix is not clearly
                    organized. The kick, bass, guitars, and other low-frequency sources
                    start competing instead of supporting each other. The result can sound
                    big in solo but unclear when the full track is playing.
                  </Typography>

                  <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                    Guitar and bass are especially easy to overbuild. Distorted guitars
                    can create a lot of low-mid density, while bass can fill both the deep
                    lows and the note-definition range. The goal is to keep the weight that
                    helps the song and remove the extra energy that slows it down.
                  </Typography>
                </Stack>
              </Box>
            </Paper>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <AutoAwesomeRoundedIcon sx={{ color: accent }} />
                    <Typography
                      sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                    >
                      Common Low-End Problems
                    </Typography>
                  </Stack>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
                      gap: 1.25,
                    }}
                  >
                    {commonProblems.map((problem) => (
                      <Paper key={problem.title} elevation={0} sx={{ ...subtlePanelSx, p: 1.5 }}>
                        <Typography sx={{ fontWeight: 800, color: style.color4, mb: 0.5 }}>
                          {problem.title}
                        </Typography>
                        <Typography variant="body2" sx={{ color: style.color3, lineHeight: 1.65 }}>
                          {problem.description}
                        </Typography>
                      </Paper>
                    ))}
                  </Box>
                </Stack>
              </Box>
            </Paper>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                gap: 2.25,
                alignItems: "stretch",
              }}
            >
              <Paper elevation={0} sx={{ ...outerPanelSx }}>
                <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <TuneRoundedIcon sx={{ color: accent }} />
                      <Typography
                        sx={{ fontSize: "1.25rem", fontWeight: 800, color: style.color4 }}
                      >
                        Step-by-Step: Tightening the Low End
                      </Typography>
                    </Stack>

                    <Stack spacing={1.2}>
                      {steps.map((step, index) => (
                        <Paper key={step.title} elevation={0} sx={{ ...subtlePanelSx, p: 1.5 }}>
                          <Stack spacing={0.6}>
                            <Typography sx={{ fontWeight: 800, color: style.color4 }}>
                              {index + 1}. {step.title}
                            </Typography>
                            <Typography variant="body2" sx={{ color: style.color3, lineHeight: 1.65 }}>
                              {step.description}
                            </Typography>
                          </Stack>
                        </Paper>
                      ))}
                    </Stack>
                  </Stack>
                </Box>
              </Paper>

              <Paper elevation={0} sx={{ ...outerPanelSx }}>
                <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <GraphicEqRoundedIcon sx={{ color: accent }} />
                      <Typography
                        sx={{ fontSize: "1.25rem", fontWeight: 800, color: style.color4 }}
                      >
                        Useful Low-End Frequency Areas
                      </Typography>
                    </Stack>

                    <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                      These ranges are starting points. Always make the final choice in the
                      context of the full mix.
                    </Typography>

                    <Stack spacing={1.2}>
                      {frequencyAreas.map((area) => (
                        <Paper key={area.range} elevation={0} sx={{ ...subtlePanelSx, p: 1.5 }}>
                          <Stack spacing={0.7}>
                            <Chip
                              label={area.range}
                              size="small"
                              sx={{
                                alignSelf: "flex-start",
                                borderRadius: "5px",
                                color: style.color4,
                                backgroundColor: "rgba(255,255,255,0.06)",
                                border: "1px solid rgba(255,255,255,0.08)",
                                fontWeight: 700,
                              }}
                            />
                            <Typography sx={{ fontWeight: 800, color: style.color4 }}>
                              {area.title}
                            </Typography>
                            <Typography variant="body2" sx={{ color: style.color3, lineHeight: 1.65 }}>
                              {area.description}
                            </Typography>
                          </Stack>
                        </Paper>
                      ))}
                    </Stack>
                  </Stack>
                </Box>
              </Paper>
            </Box>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CompressRoundedIcon sx={{ color: accent }} />
                    <Typography
                      sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                    >
                      Tight Does Not Mean Thin
                    </Typography>
                  </Stack>

                  <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                    A common mistake is cutting too much low end in the name of clarity.
                    That can make the mix cleaner, but also smaller and less satisfying.
                    Tight low end still has weight. It just has the right weight in the
                    right place.
                  </Typography>

                  <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                    Instead of removing bass everywhere, listen for what is actually causing
                    the loose feeling. Sometimes it is a resonant guitar palm mute. Sometimes
                    it is a bass note blooming too long. Sometimes it is reverb carrying
                    unnecessary lows. The better you identify the source, the less you have
                    to over-process everything else.
                  </Typography>
                </Stack>
              </Box>
            </Paper>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                gap: 2.25,
                alignItems: "stretch",
              }}
            >
              <Paper elevation={0} sx={{ ...outerPanelSx }}>
                <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <SpeedRoundedIcon sx={{ color: accent }} />
                      <Typography
                        sx={{ fontSize: "1.25rem", fontWeight: 800, color: style.color4 }}
                      >
                        Tools That Tighten Low End
                      </Typography>
                    </Stack>

                    <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                      Low-end control usually comes from a few small decisions working
                      together, not one extreme processor setting.
                    </Typography>

                    <Stack spacing={1.2}>
                      {tools.map((tool) => (
                        <Paper key={tool.title} elevation={0} sx={{ ...subtlePanelSx, p: 1.5 }}>
                          <Typography sx={{ fontWeight: 800, color: style.color4, mb: 0.4 }}>
                            {tool.title}
                          </Typography>
                          <Typography variant="body2" sx={{ color: style.color3, lineHeight: 1.65 }}>
                            {tool.description}
                          </Typography>
                        </Paper>
                      ))}
                    </Stack>
                  </Stack>
                </Box>
              </Paper>

              <Paper elevation={0} sx={{ ...outerPanelSx }}>
                <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <TipsAndUpdatesRoundedIcon sx={{ color: accent }} />
                      <Typography
                        sx={{ fontSize: "1.25rem", fontWeight: 800, color: style.color4 }}
                      >
                        Best Practices
                      </Typography>
                    </Stack>

                    <Box
                      component="ul"
                      sx={{
                        pl: 2.5,
                        m: 0,
                        color: style.color3,
                        "& li": { mb: 1.25, lineHeight: 1.75 },
                      }}
                    >
                      {bestPractices.map((tip, index) => (
                        <li key={index}>{tip}</li>
                      ))}
                    </Box>

                    <Divider sx={{ backgroundColor: "rgba(255,255,255,0.08)" }} />

                    <Paper elevation={0} sx={{ ...subtlePanelSx, p: 1.5 }}>
                      <Stack spacing={1}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <HeadphonesRoundedIcon sx={{ color: accent }} />
                          <Typography sx={{ color: style.color4, fontWeight: 800 }}>
                            Quick listening test
                          </Typography>
                        </Stack>

                        <Typography sx={{ color: style.color3, lineHeight: 1.7 }}>
                          Listen to the chorus or biggest section at low volume. If the
                          groove still feels clear and the low end does not blur the guitars,
                          bass, and kick together, the mix is moving in the right direction.
                        </Typography>
                      </Stack>
                    </Paper>
                  </Stack>
                </Box>
              </Paper>
            </Box>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <LibraryMusicRoundedIcon sx={{ color: accent }} />
                    <Typography
                      sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                    >
                      Tools That Help
                    </Typography>
                  </Stack>

                  <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                    Afterburner is built around this kind of guitar-focused cleanup. It can
                    help tighten the low end, shape cab-style tone, and bring guitar tracks
                    into focus without forcing you to rebuild the whole chain.
                  </Typography>

                  <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                    It works especially well when a guitar tone already has the right basic
                    character, but the low end feels a little too loose, cloudy, or oversized
                    once the full mix is playing.
                  </Typography>

                  <Button
                    component={RouterLink}
                    to="/products/afterburner"
                    sx={{
                      alignSelf: "flex-start",
                      borderRadius: "5px",
                      textTransform: "none",
                      fontWeight: 700,
                      color: style.color4,
                      backgroundColor: style.accentGreen,
                      "&:hover": {
                        backgroundColor: style.accentGreen,
                        filter: "brightness(1.05)",
                      },
                    }}
                  >
                    View Afterburner
                  </Button>
                </Stack>
              </Box>
            </Paper>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <MenuBookRoundedIcon sx={{ color: accent }} />
                    <Typography
                      sx={{ fontSize: "1.25rem", fontWeight: 800, color: style.color4 }}
                    >
                      Related Topics
                    </Typography>
                  </Stack>

                  <Typography sx={{ color: style.color3, lineHeight: 1.7 }}>
                    These articles connect directly to low-end control, EQ, saturation,
                    and mix clarity.
                  </Typography>

                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {relatedLinks.map((link) => (
                      <Button
                        key={link.to}
                        component={RouterLink}
                        to={link.to}
                        sx={{
                          borderRadius: "5px",
                          textTransform: "none",
                          fontWeight: 700,
                          color: style.color4,
                          backgroundColor: "rgba(255,255,255,0.05)",
                          border: `1px solid ${accent}`,
                          "&:hover": {
                            borderColor: style.color4,
                            backgroundColor: "rgba(255,255,255,0.10)",
                          },
                        }}
                      >
                        {link.label}
                      </Button>
                    ))}
                  </Stack>
                </Stack>
              </Box>
            </Paper>
          </Stack>
        </Box>
      </Box>
    </>
  );
}